import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { ArrowDownTrayIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { fetchCustomers, fetchPasses } from "../api/queries";
import TrialEndedAlert from "../components/TrialEndedAlert";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotification } from "../contexts/NotificationContext";
import { usePlanAlert } from "../contexts/PlanAlertContext";
import { useStore } from "../contexts/StoreContext";
import usePlanTier, { tierAllows } from "../hooks/usePlanTier";
import { getApiErrorReasonMessage } from "../utils/apiErrorReasons";
import CustomerEditDrawer from "./customers/CustomerEditDrawer";
import CustomerFilters from "./customers/CustomerFilters";
import CustomerPanel from "./customers/CustomerPanel";
import CustomerTable from "./customers/CustomerTable";
import DeleteCustomerDialog from "./customers/DeleteCustomerDialog";
import ExportDialog from "./customers/ExportDialog";
import SendPassModal from "./customers/SendPassModal";
import { isEditBlocked } from "./customers/customerFormat";
import { buttonClass } from "./customers/styles";

const EMPTY_PAGINATION = {
  current_page: 1,
  total_pages: 1,
  total_count: 0,
  next_page: null,
  prev_page: null,
};

// Every filter, the store and the page are part of the key, so each
// combination is its own cache entry; invalidating ["customers", storeId]
// refreshes all of them.
const customersQueryKey = (storeId, filters) => ["customers", storeId, filters];

// Switching store remounts the page, so filters, page and selection start
// over for the new store.

// Fields of the update response that the list row shares (the rest of the
// default view, notably the full `pass`, has another shape than view=list).
const SAVED_FIELDS = [
  "name", "email", "phone", "phone_country_code", "birthday", "point",
  "expires_at", "card_expired", "is_marketing_opted_in", "custom_fields",
];

function pick(source, keys) {
  return Object.fromEntries(keys.filter((key) => key in source).map((key) => [key, source[key]]));
}

export default function Customers() {
  const { selectedStore } = useStore();
  return <CustomersPage key={selectedStore?.id ?? "none"} />;
}

function CustomersPage() {
  const { t } = useLanguage();
  const { selectedStore, canManageStore } = useStore();
  const { showNotification } = useNotification();
  const { showPlanAlert } = usePlanAlert();
  const storeId = selectedStore?.id;
  const segmentsAllowed = tierAllows(usePlanTier(selectedStore), "insights");

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [passId, setPassId] = useState("all");
  const [segment, setSegment] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sendingTo, setSendingTo] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [trialAlertOpen, setTrialAlertOpen] = useState(false);
  const panelRef = useRef(null);

  // Search runs at 3+ characters (or when cleared), 500ms after typing stops.
  const commitQuery = useMemo(
    () =>
      debounce((value) => {
        setQuery(value);
        setPage(1);
      }, 500),
    [],
  );
  useEffect(() => () => commitQuery.cancel(), [commitQuery]);

  const searchable = (value) => value.trim().length >= 3 || value.trim().length === 0;

  const handleSearchChange = (value) => {
    setSearch(value);
    // Below 3 characters drop a commit still pending from a longer value, or
    // the list would filter by text the box no longer shows.
    if (searchable(value)) commitQuery(value.trim());
    else commitQuery.cancel();
  };

  const handleSearchCommit = () => {
    if (searchable(search)) {
      commitQuery.cancel();
      if (search.trim() !== query) {
        setQuery(search.trim());
        setPage(1);
      }
    }
  };

  const changePass = (id) => {
    setPassId(id);
    setPage(1);
  };

  const changeSegment = (id) => {
    setSegment(id);
    setPage(1);
  };

  const clearFilters = () => {
    commitQuery.cancel();
    setSearch("");
    setQuery("");
    setPassId("all");
    setSegment(null);
    setPage(1);
  };

  // Below Growth the API refuses `segment` (403 plan_feature_locked), so a
  // locked chip never sends one: it opens the plan dialog instead.
  const openSegmentsUpsell = () => {
    const plan = t("pricing.tier.growth.name");
    showPlanAlert({
      title: t("common.planAlertTitle"),
      description: t("common.planAlertDescription", { plan }),
      plan,
    });
  };

  const activeSegment = segmentsAllowed ? segment : null;
  const filters = { page, query, passId, segment: activeSegment };
  const hasFilters = !!query || passId !== "all" || !!activeSegment;

  const list = useQuery({
    queryKey: customersQueryKey(storeId, filters),
    queryFn: () => fetchCustomers(storeId, filters),
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });

  const { data: passes } = useQuery({
    queryKey: ["passes", storeId],
    queryFn: () => fetchPasses(storeId),
    enabled: !!storeId,
  });

  const customers = list.data?.customers || [];
  const pagination = list.data?.pagination || EMPTY_PAGINATION;
  const emptyStore = !hasFilters && !list.isPlaceholderData && list.isSuccess && pagination.total_count === 0;
  // The row as the latest page has it (an edit refreshes it), or as it was
  // picked when it is no longer on the page.
  const selectedRow = selected ? customers.find((c) => c.id === selected.id) || selected : null;

  // The list shrank under the open page (a delete emptied the last one): go
  // to the new last page instead of showing an empty one with no way back.
  const totalPages = list.data?.pagination?.total_pages;
  useEffect(() => {
    if (!list.isPlaceholderData && totalPages >= 1 && page > totalPages) {
      setPage(totalPages);
    }
  }, [list.isPlaceholderData, totalPages, page]);

  // The panel stacks under the list on narrow screens: bring it into view.
  useEffect(() => {
    if (selected) panelRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (customer) => {
    if (isEditBlocked(selectedStore)) {
      setTrialAlertOpen(true);
      return;
    }
    setEditing(customer);
  };

  const handleSendPassClose = (result) => {
    setSendingTo(null);
    if (result?.sentTo) {
      showNotification(
        t("customers.sendPass.title"),
        t("customers.sendPass.success", { email: result.sentTo }),
        "success",
        [],
        5000,
      );
    }
  };

  const listError = list.isError
    ? getApiErrorReasonMessage(list.error?.response?.data, t) || t("customers.list.error")
    : null;

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="m-0 text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
            {t("customers.title")}
          </h1>
          <p className="m-0 text-[15px] text-gray-700">{t("customers.list.subtitle")}</p>
        </div>
        {canManageStore && !emptyStore && (
          <button type="button" onClick={() => setExportOpen(true)} className={buttonClass.secondary}>
            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            {t("customers.export.button")}
          </button>
        )}
      </header>

      {emptyStore ? (
        <section
          aria-labelledby="customers-empty-title"
          className="flex flex-col items-center gap-3.5 rounded-[14px] border-[1.5px] border-dashed border-gray-300 bg-white px-6 py-14 text-center"
        >
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-indigo-50 text-indigo-700" aria-hidden="true">
            <UserGroupIcon className="h-6 w-6" />
          </span>
          <h2 id="customers-empty-title" className="m-0 text-[19px] font-semibold text-gray-900">
            {t("customers.empty.title")}
          </h2>
          <p className="m-0 max-w-[460px] text-sm leading-relaxed text-gray-600">{t("customers.empty.body")}</p>
          <div className="mt-1 flex flex-wrap justify-center gap-2">
            <Link to="/forms" className={buttonClass.primary}>
              {t("customers.empty.shareForm")}
            </Link>
            <Link to="/flyers" className={buttonClass.secondary}>
              {t("customers.empty.printFlyer")}
            </Link>
          </div>
        </section>
      ) : (
        <>
          <CustomerFilters
            search={search}
            onSearchChange={handleSearchChange}
            onSearchCommit={handleSearchCommit}
            passes={passes}
            passId={passId}
            onPassChange={changePass}
            segment={activeSegment}
            onSegmentChange={changeSegment}
            segmentsLocked={!segmentsAllowed}
            onLockedSegment={openSegmentsUpsell}
            hasFilters={hasFilters}
            onClear={clearFilters}
          />

          <div className="flex flex-wrap items-start gap-4">
            {listError ? (
              <section
                role="alert"
                className="flex min-w-0 flex-[999_1_560px] flex-col items-center gap-3 rounded-[14px] border border-gray-200 bg-white px-6 py-10 text-center"
              >
                <p className="m-0 text-sm text-gray-700">{listError}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <button type="button" onClick={() => list.refetch()} className={buttonClass.secondary}>
                    {t("customers.list.retry")}
                  </button>
                  {hasFilters && (
                    <button type="button" onClick={clearFilters} className={buttonClass.secondary}>
                      {t("customers.filter.clear")}
                    </button>
                  )}
                </div>
              </section>
            ) : (
              <CustomerTable
                customers={customers}
                pagination={pagination}
                loading={list.isLoading}
                stale={list.isPlaceholderData}
                filtered={hasFilters}
                showSegments={segmentsAllowed}
                selectedId={selectedRow?.id}
                onSelect={setSelected}
                onPage={setPage}
                empty={
                  !list.isLoading && customers.length === 0 ? (
                    <p className="m-0 px-4 py-8 text-center text-sm text-gray-600">
                      {t("customers.empty.filtered")}{" "}
                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="font-semibold text-indigo-700 hover:text-indigo-800"
                        >
                          {t("customers.filter.clear")}
                        </button>
                      )}
                    </p>
                  ) : null
                }
              />
            )}

            {selectedRow && (
              <CustomerPanel
                ref={panelRef}
                customer={selectedRow}
                showSegments={segmentsAllowed}
                canManage={canManageStore}
                onClose={() => setSelected(null)}
                onSendPass={() => setSendingTo(selectedRow)}
                onEdit={() => startEdit(selectedRow)}
                onDelete={() => setDeleting(selectedRow)}
              />
            )}
          </div>
        </>
      )}

      <SendPassModal
        isOpen={!!sendingTo}
        onClose={handleSendPassClose}
        customer={sendingTo}
        storeId={storeId}
      />

      {canManageStore && (
        <>
          <CustomerEditDrawer
            open={!!editing}
            customer={editing}
            passType={editing?.pass?.type}
            onClose={() => setEditing(null)}
            onSaved={(saved) => {
              // The saved record (default view) refreshes the panel's
              // snapshot, which is what it shows once the edit moves the row
              // off the current page or out of the filter. `pass` stays the
              // list's summary shape.
              if (saved) {
                setSelected((prev) =>
                  prev && prev.id === saved.id ? { ...prev, ...pick(saved, SAVED_FIELDS) } : prev,
                );
              }
              showNotification(t("customers.edit.saved"), "", "success", [], 4000);
            }}
          />
          <DeleteCustomerDialog
            open={!!deleting}
            customer={deleting}
            storeId={storeId}
            onClose={() => setDeleting(null)}
            onDeleted={(customer) => {
              setDeleting(null);
              setSelected(null);
              showNotification(
                t("customers.delete.success", { name: customer?.name || t("customers.unnamed") }),
                "",
                "success",
                [],
                4000,
              );
            }}
          />
          {/* Mounted per opening, so each export starts from the defaults. */}
          {exportOpen && (
            <ExportDialog open storeId={storeId} onClose={() => setExportOpen(false)} />
          )}
        </>
      )}

      <TrialEndedAlert
        isOpen={trialAlertOpen}
        onClose={() => setTrialAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </div>
  );
}
